import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Search } from 'lucide-react';
import { supabase } from '../../services/adminService';

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));
const fmtDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
};

const WFHTelecallerDetailReport = () => {
  const { telecallerId } = useParams();
  const [sp] = useSearchParams();

  const teamLeadId = sp.get('teamLeadId') || '';
  const batch = sp.get('batch') || '';
  const from = sp.get('from') || '';
  const to = sp.get('to') || '';
  const tcName = sp.get('name') || '';
  const tcEmail = sp.get('email') || '';

  const [q, setQ] = useState('');

  const { data: rows = [], isLoading, error, refetch } = useQuery({
    queryKey: ['wfhTelecallerDetail', telecallerId, teamLeadId, batch, from, to],
    enabled: !!telecallerId && !!teamLeadId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_wfh_telecaller_activity', {
        p_team_lead_id: teamLeadId,
        p_telecaller_id: telecallerId,
        p_batch_name: batch || null,
        p_from_date: from || null,
        p_to_date: to || null,
      });
      if (error) throw error;
      return data || [];
    },
  });

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return (rows || []).filter((r) => {
      const hay = [
        r.name,
        r.contact_number,
        r.alternate_number,
        r.car_number,
        r.car_model,
        r.state,
        r.pin_code,
        r.status,
        r.disposition,
        r.disposition_notes,
        r.batch_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(qq);
    });
  }, [rows, q]);

  const summary = useMemo(() => {
    const out = { total: 0, disposed: 0, callback: 0, noDisp: 0 };
    (rows || []).forEach((r) => {
      out.total += 1;
      if (r.disposition) out.disposed += 1;
      if (r.disposition === 'callback') out.callback += 1;
      if (!r.disposition) out.noDisp += 1;
    });
    return out;
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Link
              to="/wfh-employee-report"
              className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
            >
              <ArrowLeft size={16} />
              Back to report
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">WFH Telecaller Detail</h1>
          <p className="text-gray-600 mt-1">
            <span className="font-medium text-gray-900">{tcName ? tcName : fmt(telecallerId)}</span>
            {tcEmail ? <span className="text-gray-500"> · {tcEmail}</span> : null}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Filters: TeamLead {teamLeadId}
            {batch ? ` · Batch ${batch}` : ' · All batches'}
            {from || to ? ` · Date ${from || '—'} → ${to || '—'}` : null}
          </p>
        </div>

        <button
          onClick={() => refetch()}
          className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
          disabled={isLoading}
        >
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Total leads</div>
            <div className="text-xl font-bold text-gray-900">{summary.total}</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Disposed</div>
            <div className="text-xl font-bold text-gray-900">{summary.disposed}</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Callback</div>
            <div className="text-xl font-bold text-gray-900">{summary.callback}</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="text-xs text-gray-500">No disposition</div>
            <div className="text-xl font-bold text-gray-900">{summary.noDisp}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="relative w-full max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name/phone/car/disposition..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="text-sm text-gray-600">{filtered.length} rows</div>
        </div>

        {!teamLeadId ? (
          <div className="text-gray-600">Missing Team Lead filter. Please open this page from WFH Employee Report.</div>
        ) : isLoading ? (
          <div className="text-gray-600">Loading...</div>
        ) : error ? (
          <div className="text-red-600">
            Failed to load detail report: {String(error?.message || error)}
            <div className="text-xs text-gray-500 mt-2">
              If you just added this feature, ensure SQL function <code>admin_wfh_telecaller_activity</code> is deployed.
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-gray-600">No data found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disposition</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Claimed At</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disposition At</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr key={String(r.id)} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{fmt(r.name)}</div>
                      <div className="text-xs text-gray-500">
                        {fmt(r.car_model)} {r.car_number ? `· ${r.car_number}` : ''} {r.state ? `· ${r.state}` : ''}{' '}
                        {r.pin_code ? `· ${r.pin_code}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div>{fmt(r.contact_number)}</div>
                      <div className="text-xs text-gray-500">{fmt(r.alternate_number)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmt(r.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="font-medium">{fmt(r.disposition)}</div>
                      <div className="text-xs text-gray-500">{fmt(r.disposition_notes)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmt(r.batch_name)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtDateTime(r.claimed_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtDateTime(r.disposition_updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WFHTelecallerDetailReport;

